/**
 * Flat postMessage contract between the admin panel and preview runtime.
 *
 * Runtime messages intentionally use the production wire shape:
 *   { type: 'field-update', sectionId, field, value }
 *
 * Do not replace this with a wrapped `{ type, payload, timestamp }` envelope
 * unless both the admin panel and runtime are migrated together.
 */

export const PARENT_TO_RUNTIME_MESSAGE_TYPES = [
  'full-update',
  'field-update',
  'section-toggle',
  'style-update',
  'viewport-change',
  'section-highlight',
  'scroll-to-section',
] as const;

export const RUNTIME_TO_PARENT_MESSAGE_TYPES = [
  'ready',
  'component-selected',
  'element-selected',
  'field-edited',
  'image-replace-requested',
  'deselect',
  'navigate-request',
] as const;

export type ParentToRuntimeMessageType = typeof PARENT_TO_RUNTIME_MESSAGE_TYPES[number];
export type RuntimeToParentMessageType = typeof RUNTIME_TO_PARENT_MESSAGE_TYPES[number];

export type FullUpdateMessage = {
  type: 'full-update';
  data: Record<string, unknown>;
  sections?: unknown;
  editabilityMap?: unknown;
  isViewOnly?: boolean;
  viewport?: string;
};

export type FieldUpdateMessage = {
  type: 'field-update';
  sectionId: string;
  field: string;
  value: unknown;
};

export type SectionToggleMessage = {
  type: 'section-toggle';
  sectionId: string;
  enabled: boolean;
};

export type StyleUpdateMessage = {
  type: 'style-update';
  sectionId: string;
  field: string;
  styles: unknown;
};

export type ViewportChangeMessage = {
  type: 'viewport-change';
  viewport: string;
};

export type SectionHighlightMessage = {
  type: 'section-highlight';
  sectionId: string;
};

export type ScrollToSectionMessage = {
  type: 'scroll-to-section';
  sectionId: string;
};

export type ParentToRuntimeMessage =
  | FullUpdateMessage
  | FieldUpdateMessage
  | SectionToggleMessage
  | StyleUpdateMessage
  | ViewportChangeMessage
  | SectionHighlightMessage
  | ScrollToSectionMessage;

export type ReadyMessage = {
  type: 'ready';
  [key: string]: unknown;
};

export type ComponentSelectedMessage = {
  type: 'component-selected';
  sectionId: string;
  fieldPath: string;
  pageId?: string;
  fieldType?: string;
};

export type ElementSelectedMessage = {
  type: 'element-selected';
  sectionId: string;
  field: string;
  elementType?: string;
  content?: string;
  itemIndex?: number;
};

export type FieldEditedMessage = {
  type: 'field-edited';
  sectionId: string;
  field: string;
  value: unknown;
  listName?: string;
  itemIndex?: number;
};

export type ImageReplaceRequestedMessage = {
  type: 'image-replace-requested';
  sectionId: string;
  field: string;
};

export type DeselectMessage = {
  type: 'deselect';
};

export type NavigateRequestMessage = {
  type: 'navigate-request';
  pathname: string;
};

export type RuntimeToParentMessage =
  | ReadyMessage
  | ComponentSelectedMessage
  | ElementSelectedMessage
  | FieldEditedMessage
  | ImageReplaceRequestedMessage
  | DeselectMessage
  | NavigateRequestMessage;

export type PreviewMessage = ParentToRuntimeMessage | RuntimeToParentMessage;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isParentToRuntimeMessageType(type: unknown): type is ParentToRuntimeMessageType {
  return typeof type === 'string' && PARENT_TO_RUNTIME_MESSAGE_TYPES.includes(type as ParentToRuntimeMessageType);
}

export function isRuntimeToParentMessageType(type: unknown): type is RuntimeToParentMessageType {
  return typeof type === 'string' && RUNTIME_TO_PARENT_MESSAGE_TYPES.includes(type as RuntimeToParentMessageType);
}

export function isParentToRuntimeMessage(message: unknown): message is ParentToRuntimeMessage {
  if (!isRecord(message) || !isParentToRuntimeMessageType(message.type)) return false;

  switch (message.type) {
    case 'full-update':
      return isRecord(message.data);
    case 'field-update':
      return typeof message.sectionId === 'string' && typeof message.field === 'string';
    case 'section-toggle':
      return typeof message.sectionId === 'string' && typeof message.enabled === 'boolean';
    case 'style-update':
      return typeof message.sectionId === 'string' && typeof message.field === 'string';
    case 'viewport-change':
      return typeof message.viewport === 'string';
    case 'section-highlight':
    case 'scroll-to-section':
      return typeof message.sectionId === 'string';
  }
}

export function isRuntimeToParentMessage(message: unknown): message is RuntimeToParentMessage {
  if (!isRecord(message) || !isRuntimeToParentMessageType(message.type)) return false;

  switch (message.type) {
    case 'ready':
    case 'deselect':
      return true;
    case 'component-selected':
      return typeof message.sectionId === 'string' && typeof message.fieldPath === 'string';
    case 'element-selected':
    case 'field-edited':
    case 'image-replace-requested':
      return typeof message.sectionId === 'string' && typeof message.field === 'string';
    case 'navigate-request':
      return typeof message.pathname === 'string';
  }
}
